import React, { useEffect, useState, useRef } from "react";
import productAPI from "../../api/productApi";
import { Link } from "react-router-dom";
import { ShoppingBag, Loader2, Search, X } from "lucide-react"; 

function SearchCanvas({ isOpen, searchText, setSearchText, onClose }) {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const inputRef = useRef(null);

  // Focus the input element cleanly when parent signals trigger active state open
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!searchText.trim()) {
      setProducts([]);
      return;
    }

    const delay = setTimeout(() => {
      fetchProducts(1, searchText);
    }, 400);

    return () => clearTimeout(delay);
  }, [searchText]);

  const fetchProducts = async (pageNum = 1, queryText) => {
    setLoading(true);
    try {
      const res = await productAPI.listing({
        page: pageNum,
        limit: 5,
        keyword: queryText,
      });

      if (res.code === 200) {
        setProducts(res.data?.products || []);
      }
    } catch (err) {
      console.error("Error loading products:", err);
    } finally {
      setLoading(false);
    }
  };

  /* 🌟 FIXED: Only halts execution when isOpen is false. Blank query is handled by popular suggestion panels layout down below! */
  if (!isOpen) return null;

  return (
    <div className="luxury-search-full-overlay-dropdown">
      <div className="container">
        
        {/* INTEGRATED EMBEDDED EDITORIAL INPUT BAR */}
        <div className="luxury-search-input-container-row d-flex align-items-center">
          <Search size={20} className="text-muted me-3" />
          <input
            ref={inputRef}
            type="text"
            className="luxury-search-main-input-field"
            placeholder="Type to search our premium collection..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText && (
            <button type="button" className="clear-search-text-btn me-2" onClick={() => setSearchText("")}>
              <X size={16} />
            </button>
          )}
        </div>

        {/* RUNNING LOADING LOADER TRACK */}
        {loading && (
          <div className="search-feedback-state-box d-flex align-items-center justify-content-center py-5 gap-2">
            <Loader2 size={16} className="spinner-animation-loop text-muted" />
            <span className="search-state-text">Refining boutique match assets...</span>
          </div>
        )}

        {/* BLANK STATE (SUGGEST BRAND RECOMANDATIONS TABS) */}
        {!loading && !searchText.trim() && (
          <div className="luxury-search-suggestions-box py-4 animate-fade-in">
            <p className="suggestion-caption-label mb-3">Popular Searches</p>
            <div className="d-flex flex-wrap gap-2 platform-suggestion-swatches">
              <Link to="/shop" className="suggestion-pill" onClick={onClose}>Dresses</Link>
              <Link to="/shop" className="suggestion-pill" onClick={onClose}>Bags</Link>
              <Link to="/shop" className="suggestion-pill" onClick={onClose}>Woolen Dresses</Link>
              
            </div>
          </div>
        )}

        {/* ZERO RESULTS FOUND QUERY */}
        {!loading && searchText.trim() && products.length === 0 && (
          <div className="search-feedback-state-box text-center py-5">
            <div className="mb-2 opacity-40">
              <ShoppingBag size={28} strokeWidth={1} className="mx-auto" />
            </div>
            <p className="search-state-text mb-0">No statement products match "{searchText}"</p>
          </div>
        )}

        {/* DYNAMIC SEARCH LIST FOUND ITERATOR */}
        {!loading && products.length > 0 && (
          <div className="luxury-search-results-wrapper-list py-3 animate-fade-in">
            <div className="search-results-count-header mb-2">
              Collection Results ({products.length})
            </div>
            
            {products.map((product) => {
              const image = product?.thumbnail || product?.images?.[0];
              const price = product?.variants?.[0]?.price || product?.price;

              return (
                <Link
                  to={`/shop/${product.slug}`}
                  className="luxury-search-item-anchor d-flex align-items-center py-3 border-bottom"
                  key={product._id}
                  onClick={onClose}
                >
                  <div className="luxury-search-item-thumb-box me-3">
                    <img src={image} className="luxury-search-img-asset" alt={product.title} />
                  </div>
                  <div className="luxury-search-item-meta flex-grow-1">
                    <h6 className="luxury-search-title-text mb-1">{product.title}</h6>
                    <p className="luxury-search-price-text mb-0">₹{Number(price).toFixed(0)}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}

export default SearchCanvas;